import { useId, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { cn } from '../cn';

/**
 * The building blocks every BCE screen is assembled from.
 *
 * Each one is a thin wrapper over the exact classes the portal mockup uses, so
 * a screen built from these matches the client portal without its author
 * knowing a single token. They accept `className` for spacing only — the look
 * itself is deliberately not configurable.
 */

// ─── Heading ────────────────────────────────────────────────────────────────

/** The heading scale. Sizes live here so pages never restate them as classes. */
const HEADING_VARIANTS = {
  /** The page title. Use on a custom page header; plain pages use `PageHeading`. */
  page: 'text-portal-page tracking-[-1.7px]',
  /** The page title, one step down — for long, data-derived titles. */
  'page-sm': 'text-portal-page-sm tracking-[-1.3px]',
  /** 19px regular — a page section (`<h2>` by default). Use `SectionHeader` for the full block. */
  section: 'text-portal-section',
  /** 15px — a card or dialog title. */
  card: 'text-portal-card-title font-bold',
} as const;

export interface HeadingProps {
  readonly variant: keyof typeof HEADING_VARIANTS;
  /** The rendered element, for document outline. Defaults to `h2`. */
  readonly as?: 'h1' | 'h2' | 'h3' | 'h4';
  /** Layout-only additions (margins). Typography stays in the variant. */
  readonly className?: string;
  readonly children: React.ReactNode;
}

/**
 * A standard heading. Every non-page-title heading renders through this, so
 * heading typography has one source of truth — raw `<h2 className="…">` in a
 * page is a missing variant here, not a styling opportunity there.
 *
 * @param props - Variant, element, layout classes and content.
 * @returns The heading element.
 */
export function Heading({
  variant,
  as: Tag = 'h2',
  className,
  children,
}: HeadingProps): React.ReactElement {
  return <Tag className={cn(HEADING_VARIANTS[variant], className)}>{children}</Tag>;
}

// ─── CardHeader ─────────────────────────────────────────────────────────────

export interface CardHeaderProps {
  readonly title: string;
  /** Always present: a card that cannot say what it is for should not exist. */
  readonly description: string;
  /** A progress figure or a pill, sat on the title's baseline at the right. */
  readonly aside?: React.ReactNode;
  /** A pill beside the title itself, saying what kind of thing this is: who sees it, what state it is in. */
  readonly badge?: React.ReactNode;
  readonly as?: 'h2' | 'h3';
  readonly className?: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * The heading block at the top of a card: a title, the one-line description
 * beneath it, and an optional aside on the right. One component so every
 * card in the portal opens the same way — same sizes, same spacing — and a
 * change of mind about either is made once.
 */
export function CardHeader({
  title,
  description,
  aside,
  badge,
  as = 'h2',
  className,
  guide,
}: CardHeaderProps): React.ReactElement {
  return (
    <div data-guide={guide} className={cn('mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2', className)}>
      <div className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <Heading variant="card" as={as}>
            {title}
          </Heading>
          {badge === undefined || badge === null ? null : badge}
        </span>
        <p className="mt-1 text-portal-tiny leading-[1.6] text-portal-muted">{description}</p>
      </div>
      {aside === undefined || aside === null ? null : (
        <div className="shrink-0 text-portal-micro text-portal-muted">{aside}</div>
      )}
    </div>
  );
}

// ─── SectionHeader ──────────────────────────────────────────────────────────

export interface SectionHeaderProps {
  readonly title: string;
  /** Always present: one line saying what the section holds. */
  readonly description: string;
  /** A count or a link, sat at the right on the description's baseline. */
  readonly aside?: React.ReactNode;
  readonly as?: 'h2' | 'h3';
  readonly className?: string;
}

/**
 * The heading block for a page section: the title and a one-line description
 * sit on the page, above the panel that holds the section's content. The
 * counterpart of {@link CardHeader}, which titles content inside a card. Every
 * section on a screen opens this way, so no two read as different kinds of
 * thing.
 *
 * @param props - Title, description and an optional aside.
 * @returns The section header.
 */
export function SectionHeader({
  title,
  description,
  aside,
  as = 'h2',
  className,
}: SectionHeaderProps): React.ReactElement {
  return (
    <div className={cn('mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-1', className)}>
      <div className="min-w-0">
        <Heading variant="section" as={as}>
          {title}
        </Heading>
        <p className="text-portal-micro text-portal-muted">{description}</p>
      </div>
      {aside === undefined || aside === null ? null : (
        <div className="shrink-0 text-portal-micro text-portal-muted">{aside}</div>
      )}
    </div>
  );
}

// ─── Panel ──────────────────────────────────────────────────────────────────

export interface PanelProps {
  readonly className?: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
  readonly children: React.ReactNode;
}

/**
 * The rounded white card everything sits in.
 *
 * @param props - Content and optional spacing classes.
 * @returns The panel.
 */
export function Panel({ className, guide, children }: PanelProps): React.ReactElement {
  return (
    <div
      data-guide={guide}
      className={cn(
        // min-w-0: as a grid or flex item a card takes its track's width, never
        // its widest content's — a scrolling table inside must not stretch the
        // card (and the page) past a phone's edge.
        'min-w-0 rounded-portal-card border border-portal-line bg-white shadow-portal-card',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─── Pill ───────────────────────────────────────────────────────────────────

/** The portal's status colors, named by meaning rather than by color. */
export type PillTone = 'success' | 'warning' | 'danger' | 'brand' | 'info' | 'neutral';

const PILL_TONES: Readonly<Record<PillTone, string>> = {
  success: 'bg-portal-success-bg text-portal-success',
  warning: 'bg-amber-50 text-amber-800',
  danger: 'bg-rose-50 text-rose-700',
  brand: 'bg-brand-tint text-brand',
  info: 'bg-violet-50 text-violet-700',
  neutral: 'bg-slate-100 text-slate-600',
};

export interface PillProps {
  readonly tone?: PillTone;
  readonly className?: string;
  readonly children: React.ReactNode;
}

/**
 * A status pill.
 *
 * @param props - Tone and label.
 * @returns The pill.
 */
export function Pill({ tone = 'neutral', className, children }: PillProps): React.ReactElement {
  return (
    <span
      className={cn(
        'inline-block rounded-portal-pill px-2 py-0.5 text-portal-micro',
        PILL_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ─── Buttons ────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

const BUTTON_VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  primary: 'bg-brand text-white font-bold shadow-portal-button',
  // Sets its own text color: inheriting it left the label white-on-white inside
  // a dark band (the catalog's "Planning something bigger?", 2026-09-26).
  secondary: 'border border-[#cad3df] bg-white text-portal-ink',
  ghost: 'text-brand',
};

/** Shared box classes for every button/link-button. */
const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-portal-button text-portal-small transition-opacity disabled:opacity-50';

/** Height/padding per size. Default matches the mockup's 43px button. */
const BUTTON_SIZES = {
  default: 'h-[43px] px-5',
  /** Denser rows and toolbars — same type size, less chrome. */
  compact: 'h-[34px] px-3',
} as const;

export type ButtonSize = keyof typeof BUTTON_SIZES;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly className?: string;
  /**
   * Render a router `<Link>` styled as a button, for in-app navigation that
   * must stay client-side (a real link, middle-clickable, no full reload).
   * Takes precedence over `href`.
   */
  readonly to?: string;
  /** Router state for a `to` link, e.g. `useFromHere()` so the next page's Back returns here. */
  readonly state?: unknown;
  /**
   * Render an `<a href>` styled as a button — for external links or new-tab
   * navigation. Anchor-only attributes (`target`, `rel`, `download`) apply.
   */
  readonly href?: string;
  readonly target?: string;
  readonly rel?: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * The portal's button: 43px tall, 6px radius, brand-filled by default.
 *
 * A native `<button>` with the mockup's styling — or, when `href` is given, an
 * `<a>` wearing the same styling, so links that must stay links (open in a new
 * tab, be middle-clickable) still look like buttons instead of forcing a
 * hand-styled anchor.
 *
 * @param props - Native button props plus the variant, or `href` for a link.
 * @returns The button or link-button.
 */
export function Button({
  variant = 'primary',
  size = 'default',
  className,
  type,
  to,
  state,
  href,
  target,
  rel,
  guide,
  ...rest
}: ButtonProps): React.ReactElement {
  const classes = cn(BUTTON_BASE, BUTTON_SIZES[size], BUTTON_VARIANTS[variant], className);

  if (to !== undefined) {
    return (
      <Link to={to} state={state} className={classes} data-guide={guide} aria-label={rest['aria-label']}>
        {rest.children}
      </Link>
    );
  }

  if (href !== undefined) {
    const anchorRest = rest as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a href={href} target={target} rel={rel} className={classes} data-guide={guide} {...anchorRest}>
        {rest.children}
      </a>
    );
  }

  return (
    <button
      // Explicit default: a bare <button> inside a form submits it, which is
      // almost never what a "Cancel" button intends.
      type={type ?? 'button'}
      className={classes}
      data-guide={guide}
      {...rest}
    />
  );
}

// ─── Form fields ────────────────────────────────────────────────────────────

export interface FieldProps {
  readonly label: string;
  /** Ties the label to its control. Must match the control's `id`. */
  readonly htmlFor: string;
  readonly hint?: string;
  /**
   * Where the hint sits. `below` (the default) is the usual afterthought —
   * a format note read once the control is in view. `above` is for a hint
   * that explains what the field is *for*: on a form someone fills in from
   * scratch, that has to be readable before they start typing, not after.
   */
  readonly hintPosition?: 'above' | 'below';
  /** Sits on the label's row, right-aligned — a reset link, say. */
  readonly action?: React.ReactNode;
  /** Marks the label so the person can see which fields the form will not save without. */
  readonly required?: boolean;
  readonly className?: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
  readonly children: React.ReactNode;
}

/**
 * A labelled form field.
 *
 * @param props - Label, target control id, optional hint and the control.
 * @returns The field.
 */
export function Field({
  label,
  htmlFor,
  hint,
  hintPosition = 'below',
  action,
  required = false,
  className,
  guide,
  children,
}: FieldProps): React.ReactElement {
  const hintNode =
    hint === undefined ? null : (
      <p
        className={cn(
          'text-portal-micro text-portal-muted',
          hintPosition === 'above' ? 'mt-0.5' : 'mt-1',
        )}
      >
        {hint}
      </p>
    );

  return (
    <div className={className} data-guide={guide}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="block text-portal-tiny font-bold">
          {label}
          {required ? (
            <span className="ml-0.5 text-rose-600" title="Required">
              <span aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </span>
          ) : null}
        </label>
        {action}
      </div>
      {hintPosition === 'above' ? hintNode : null}
      {children}
      {hintPosition === 'below' ? hintNode : null}
    </div>
  );
}

/**
 * Field density. `default` is the standalone form control (43px, with a top
 * margin so it sits under a label); `compact` is the shorter control that
 * lives inline in a table row or toolbar (34px, no top margin).
 */
export type FieldDensity = 'default' | 'compact';

/** Shared box classes for text/select inputs, by density. */
// 16px on phones, the design sizes from 720px up: iOS Safari auto-zooms any
// focused control whose font is smaller than 16px, and locking the viewport
// instead would break pinch-zoom (an accessibility floor, WCAG 1.4.4).
const INPUT_BOX: Readonly<Record<FieldDensity, string>> = {
  default: 'mt-1.5 h-[43px] px-3 text-[16px] min-[720px]:text-portal-small',
  compact: 'h-[34px] px-2.5 text-[16px] min-[720px]:text-portal-micro',
};

/**
 * The border/radius/background every input control shares. `font-normal`
 * because a label often wraps its control, and Tailwind preflight makes form
 * controls inherit font — without it the field text would render bold.
 */
const INPUT_SURFACE = 'w-full rounded-portal-input border border-[#cad3df] bg-white font-normal';

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  /** React 19 passes `ref` as a prop; the spread forwards it to the element. */
  readonly ref?: React.Ref<HTMLInputElement>;
  readonly className?: string;
  /** Control size. `compact` for inline table/toolbar use. */
  readonly density?: FieldDensity;
}

/**
 * The portal's text input.
 *
 * @param props - Native input props plus optional density.
 * @returns The input.
 */
export function TextInput({
  className,
  density = 'default',
  ...rest
}: TextInputProps): React.ReactElement {
  return <input className={cn(INPUT_SURFACE, INPUT_BOX[density], className)} {...rest} />;
}

export interface SelectInputProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  /** Control size. `compact` for inline table/toolbar use. */
  readonly density?: FieldDensity;
  readonly className?: string;
}

/**
 * The portal's select, matched to {@link TextInput}.
 *
 * @param props - Native select props.
 * @returns The select.
 */
export function SelectInput({
  className,
  density = 'default',
  ...rest
}: SelectInputProps): React.ReactElement {
  // The chevron and its gutter come from the base `select` rule in theme.css,
  // so every select — this one and the raw selects in older screens — carries
  // the same affordance. Here we only set the field's own box.
  return (
    <select
      className={cn(INPUT_SURFACE, INPUT_BOX[density], 'disabled:opacity-40', className)}
      {...rest}
    />
  );
}

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  /** React 19 passes `ref` as a prop; the spread forwards it to the element. */
  readonly ref?: React.Ref<HTMLTextAreaElement>;
  readonly className?: string;
}

/**
 * The portal's multiline input, matched to {@link TextInput}. Rows via the
 * native `rows` prop; height grows from there.
 *
 * @param props - Native textarea props.
 * @returns The textarea.
 */
export function TextArea({ className, ...rest }: TextAreaProps): React.ReactElement {
  return (
    <textarea
      className={cn(INPUT_SURFACE, 'mt-1.5 p-3 text-[16px] min-[720px]:text-portal-small', className)}
      {...rest}
    />
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

/**
 * A column header: a plain string for an unsortable column, or a labelled
 * sort key for one the caller can order by.
 */
export type DataTableHeader =
  | string
  | {
      readonly label: string;
      /** Omit for a labeled but unsortable column. */
      readonly sortKey?: string;
      /** A small node (e.g. an info tooltip) rendered beside the label. */
      readonly info?: React.ReactNode;
    };

/** The active sort a DataTable displays. */
export interface DataTableSort {
  readonly key: string;
  readonly direction: 'asc' | 'desc';
}

/**
 * A label over several adjacent columns, in a row above the headers: says
 * which columns share a meaning (e.g. "Last 90 days" over the windowed ones,
 * "Now" over the point-in-time ones). An empty label leaves its span blank.
 */
export interface DataTableHeaderGroup {
  readonly label: string;
  /** How many columns it covers, left to right; the spans add up to the header count. */
  readonly span: number;
}

/** One label and value on a phone card. */
export interface DataTableCardField {
  readonly label: string;
  readonly value: React.ReactNode;
}

/**
 * A table row as a card for phones, where a wide table would hide most of its
 * columns behind a sideways scroll.
 */
export interface DataTableCard {
  readonly key: string;
  /** The row's name — what the first column shows. */
  readonly title: React.ReactNode;
  /** A muted line under the title. */
  readonly subtitle?: React.ReactNode;
  /** The row's figures, two to a line. */
  readonly fields: readonly DataTableCardField[];
  /** Opens the row, like clicking it in the table. */
  readonly onOpen?: () => void;
  /** A control under the figures (e.g. Hide), kept outside the open target. */
  readonly action?: React.ReactNode;
}

export interface DataTableProps {
  readonly headers: readonly DataTableHeader[];
  /** Optional labels over groups of columns (desktop table only; cards label each field). */
  readonly headerGroups?: readonly DataTableHeaderGroup[];
  /**
   * The same rows as cards: given, phones (below 768px) get a card list with a
   * sort menu built from the sortable headers instead of the table.
   */
  readonly cards?: readonly DataTableCard[];
  /** Index of headers that only exist for screen readers, e.g. "Actions". */
  readonly visuallyHiddenHeaders?: readonly number[];
  /**
   * Index of headers to right-align — numeric columns whose cells are
   * right-aligned, so the label sits over its values instead of across the
   * column from them.
   */
  readonly rightAlignedHeaders?: readonly number[];
  readonly minWidth?: number;
  /**
   * The active sort, when the caller sorts server-side. Controlled: clicking
   * a sortable header calls `onSort` with its key; the caller flips the
   * direction (or switches column) and re-queries.
   */
  readonly sort?: DataTableSort;
  readonly onSort?: (key: string) => void;
  /**
   * Embedded mode — for a table living inside a Panel (or any card): the
   * card owns the rounding and outer border, so the table keeps only its
   * top hairline and square corners.
   */
  readonly flush?: boolean;
  /**
   * Pins the first column while the rest scrolls sideways — for a wide grid
   * whose first column names each row (a week-by-week forecast). The header
   * cell is pinned here; the caller pins its body cells with
   * `sticky left-0 bg-white` (or the row's own background).
   */
  readonly stickyFirstColumn?: boolean;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
  readonly children: React.ReactNode;
}

/**
 * The portal's table: grey uppercase header band, hairline row dividers, and a
 * horizontal scroll container so a wide table never widens the page.
 *
 * Rows stay plain `<tr>`/`<td>` in the caller — a table's cells are where
 * screens genuinely differ, and abstracting them buys nothing but indirection.
 *
 * @param props - Column headers and the rows.
 * @returns The table in its scroll container.
 */
export function DataTable({ cards, ...props }: DataTableProps): React.ReactElement {
  const narrow = useNarrowScreen();
  if (!cards || !narrow) return <TableView {...props} />;
  return (
    <CardView
      cards={cards}
      headers={props.headers}
      sort={props.sort}
      onSort={props.onSort}
      guide={props.guide}
      flush={props.flush ?? false}
    />
  );
}

/** Below Tailwind's `md`: phones, where tables become cards. */
const NARROW_SCREEN_QUERY = '(max-width: 767.98px)';

function subscribeNarrowScreen(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const query = window.matchMedia(NARROW_SCREEN_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function narrowScreenNow(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(NARROW_SCREEN_QUERY).matches;
}

/**
 * Whether the screen is phone-width, kept current as it resizes or rotates.
 * False where there's no screen to measure (tests, server).
 *
 * @returns True below 768px.
 */
export function useNarrowScreen(): boolean {
  return useSyncExternalStore(subscribeNarrowScreen, narrowScreenNow, () => false);
}

/** The phone layout: a sort menu over one card per row. */
function CardView({
  cards,
  headers,
  sort,
  onSort,
  guide,
  flush,
}: {
  readonly cards: readonly DataTableCard[];
  readonly headers: readonly DataTableHeader[];
  readonly sort?: DataTableSort | undefined;
  readonly onSort?: ((key: string) => void) | undefined;
  readonly guide?: string | undefined;
  /** Inside a card: inset the list so row cards don't sit edge to edge with the outer one. */
  readonly flush: boolean;
}): React.ReactElement {
  const sortable = headers.flatMap((header) =>
    typeof header !== 'string' && header.sortKey !== undefined ? [{ key: header.sortKey, label: header.label }] : [],
  );
  const current = sortable.find((option) => option.key === sort?.key);
  const sortId = useId();
  return (
    <div data-guide={guide} className={flush ? 'px-4 pb-4' : undefined}>
      {onSort && sortable.length > 0 ? (
        <div className="mb-3 flex items-center gap-2">
          <label className="text-portal-micro font-bold text-portal-muted" htmlFor={sortId}>
            Sort by
          </label>
          <SelectInput
            id={sortId}
            density="compact"
            // 40px: the card list only renders on phones, where this is a finger target.
            className="h-10 min-w-0 flex-1"
            value={current?.key ?? ''}
            onChange={(event) => onSort(event.target.value)}
          >
            {current ? null : <option value="">Choose…</option>}
            {sortable.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </SelectInput>
          {current && sort ? (
            <Button
              variant="secondary"
              size="compact"
              className="h-10 w-10 px-0"
              aria-label={sort.direction === 'asc' ? 'Ascending; switch to descending' : 'Descending; switch to ascending'}
              onClick={() => onSort(current.key)}
            >
              {sort.direction === 'asc' ? <ArrowUpIcon aria-hidden className="size-4" /> : <ArrowDownIcon aria-hidden className="size-4" />}
            </Button>
          ) : null}
        </div>
      ) : null}
      <ul className="grid gap-3">
        {cards.map((card) => {
          const body = (
            <>
              <span className="flex items-start gap-2">
                <span className="min-w-0 flex-1">
                  <span className="block text-portal-small font-bold break-words text-portal-ink">{card.title}</span>
                  {card.subtitle ? <span className="block text-portal-micro text-portal-muted">{card.subtitle}</span> : null}
                </span>
                {card.onOpen ? <ChevronRightIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-portal-muted" /> : null}
              </span>
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                {card.fields.map((field) => (
                  <div key={field.label} className="min-w-0">
                    <dt className="text-portal-micro text-portal-muted">{field.label}</dt>
                    <dd className="text-portal-small text-portal-ink tabular-nums">{field.value}</dd>
                  </div>
                ))}
              </dl>
            </>
          );
          return (
            // relative: anchors sr-only text in a field to the card, not the page (see TableView).
            <li key={card.key} className="relative rounded-portal-card border border-portal-line bg-white">
              {card.onOpen ? (
                <button type="button" onClick={card.onOpen} className="block w-full p-4 text-left transition-colors hover:bg-portal-bg">
                  {body}
                </button>
              ) : (
                <div className="p-4">{body}</div>
              )}
              {card.action ? <div className="border-t border-portal-line px-4 py-2">{card.action}</div> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TableView({
  headers,
  headerGroups,
  visuallyHiddenHeaders = [],
  rightAlignedHeaders = [],
  minWidth = 640,
  sort,
  onSort,
  flush = false,
  stickyFirstColumn = false,
  guide,
  children,
}: Omit<DataTableProps, 'cards'>): React.ReactElement {
  return (
    <div
      data-guide={guide}
      className={cn(
        // [contain:layout]: a min-width table inside a scroll container still
        // leaks its width into the document's scroll area in Chromium, making
        // whole pages pan sideways on phones; layout containment stops it and
        // the wrapper keeps scrolling internally. relative: an absolutely
        // positioned sr-only label in a cell otherwise anchors to the page and
        // stretches it below the table (blank space under a scrolling list).
        'relative overflow-x-auto bg-white [contain:layout]',
        flush
          ? // 8px: the card's 9px radius minus its 1px border, so a table
            // ending a card nests inside the rounded corners instead of
            // squaring them off.
            'rounded-b-[8px] border-t border-portal-line'
          : 'rounded-portal-card border border-portal-line',
      )}
    >
      <table className="w-full border-collapse text-left" style={{ minWidth }}>
        <thead>
          {headerGroups ? (
            <tr className="bg-[#f2f5f9] text-portal-micro text-[#7a8699]">
              {headerGroups.map((group, index) => (
                <th
                  // Groups never reorder; a label can repeat, so the index keys them.
                  key={`${index}-${group.label}`}
                  scope={group.label ? 'colgroup' : undefined}
                  colSpan={group.span}
                  className="px-3 pt-2.5 pb-0 text-center font-bold"
                >
                  {group.label ? (
                    <span className="block border-b border-portal-line pb-1">{group.label}</span>
                  ) : null}
                </th>
              ))}
            </tr>
          ) : null}
          <tr className="bg-[#f2f5f9] text-portal-micro text-[#7a8699] uppercase">
            {headers.map((header, index) => {
              const label = typeof header === 'string' ? header : header.label;
              const sortKey = typeof header === 'string' ? undefined : header.sortKey;
              const info = typeof header === 'string' ? undefined : header.info;
              const active = sortKey !== undefined && sort?.key === sortKey;
              const rightAligned = rightAlignedHeaders.includes(index);
              return (
                <th
                  key={label}
                  scope="col"
                  aria-sort={
                    active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  className={cn(
                    'p-0 font-extrabold',
                    rightAligned && 'text-right',
                    stickyFirstColumn && index === 0 && 'sticky left-0 z-10 bg-[#f2f5f9]',
                  )}
                >
                  {(() => {
                    const sortChevron = active ? (
                      sort.direction === 'asc' ? (
                        <ChevronUpIcon aria-hidden className="size-3.5 shrink-0" />
                      ) : (
                        <ChevronDownIcon aria-hidden className="size-3.5 shrink-0" />
                      )
                    ) : (
                      <ChevronsUpDownIcon aria-hidden className="size-3.5 shrink-0 opacity-40" />
                    );
                    if (visuallyHiddenHeaders.includes(index)) {
                      return <span className="sr-only p-3">{label}</span>;
                    }
                    if (sortKey !== undefined && onSort) {
                      // With an info tooltip, the button is a sibling of the info
                      // (an interactive tooltip trigger cannot nest inside a button).
                      if (info) {
                        return (
                          <span className={cn('flex items-center', rightAligned && 'justify-end')}>
                            <button
                              type="button"
                              onClick={() => onSort(sortKey)}
                              className={cn(
                                'flex items-center gap-1 py-3 pl-3 font-extrabold uppercase transition-colors hover:text-portal-ink',
                                active && 'text-portal-ink',
                              )}
                            >
                              {label}
                              {sortChevron}
                            </button>
                            <span className="flex items-center pr-3 pl-1">{info}</span>
                          </span>
                        );
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => onSort(sortKey)}
                          className={cn(
                            'flex w-full items-center gap-1 p-3 font-extrabold uppercase transition-colors hover:text-portal-ink',
                            rightAligned && 'justify-end',
                            active && 'text-portal-ink',
                          )}
                        >
                          {label}
                          {sortChevron}
                        </button>
                      );
                    }
                    return (
                      <span className={cn('flex items-center gap-1 p-3', rightAligned && 'justify-end')}>
                        {label}
                        {info}
                      </span>
                    );
                  })()}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ─── States ─────────────────────────────────────────────────────────────────

export interface SkeletonProps {
  /** Extra classes for size and shape — width/height/rounding. */
  readonly className?: string;
}

/**
 * A single shimmering placeholder block. Compose these into skeletons that
 * echo the shape of the content still loading, so the layout doesn't jump
 * when data arrives. Decorative — hidden from the accessibility tree; the
 * surrounding region owns the `role="status"` announcement.
 *
 * @param props - Sizing classes.
 * @returns The placeholder block.
 */
export function Skeleton({ className }: SkeletonProps): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      className={cn('portal-skeleton block rounded-portal-button', className)}
    />
  );
}

export interface SkeletonTableProps {
  /** Number of placeholder rows. Default 6. */
  readonly rows?: number;
  /** Relative column widths, as Tailwind basis/width classes. */
  readonly columns?: readonly string[];
  readonly label?: string;
}

/**
 * A table-shaped loading placeholder: a header strip and shimmering rows that
 * match a DataTable's rhythm, so a list swaps in without the page reflowing.
 *
 * @param props - Row count, column widths and an accessible label.
 * @returns The skeleton table.
 */
export function SkeletonTable({
  rows = 6,
  columns = ['w-[32%]', 'w-[24%]', 'w-[16%]', 'w-[14%]'],
  label = 'Loading…',
}: SkeletonTableProps): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="rounded-portal-card border border-portal-line bg-white shadow-portal-card"
    >
      <span className="sr-only">{label}</span>
      <div className="flex gap-4 border-b border-portal-line px-3 py-3">
        {columns.map((width, index) => (
          <Skeleton key={index} className={cn('h-3', width)} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 border-b border-portal-line px-3 py-3.5 last:border-b-0">
          {columns.map((width, index) => (
            <Skeleton key={index} className={cn('h-3.5', width)} />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface LoadingStateProps {
  readonly label: string;
  /** Hide the spinner and show only the label — for tight inline slots. */
  readonly minimal?: boolean;
}

/**
 * An in-progress placeholder: a spinner over the label. Prefer a
 * {@link SkeletonTable} for lists (it holds the layout); use this for actions
 * and small regions where a shaped skeleton would be overkill.
 *
 * @param props - What is loading (announced to screen readers) and density.
 * @returns The placeholder.
 */
export function LoadingState({ label, minimal = false }: LoadingStateProps): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center justify-center gap-2.5 text-portal-small text-portal-muted',
        minimal ? 'py-2' : 'p-8',
      )}
    >
      <span
        aria-hidden="true"
        className="size-4 shrink-0 animate-spin rounded-full border-2 border-portal-line border-t-brand"
      />
      {label}
    </div>
  );
}

export interface ErrorNoticeProps {
  /**
   * Panel heading. Optional — omit it in inline mode (which shows only the
   * message), and the panel variant simply drops the heading when absent.
   */
  readonly title?: string;
  readonly message: string;
  /** Render the compact banner instead of a full panel. */
  readonly inline?: boolean;
  /** Inline only: rose for a failure (default), amber for a caution the person should read before going on. */
  readonly tone?: 'danger' | 'warning';
  /** Optional action: a retry Button below a panel error, or the fix beside an inline notice. */
  readonly action?: React.ReactNode;
}

/**
 * A failure the person can read, in place of the content that failed.
 *
 * @param props - What failed and why.
 * @returns The notice.
 */
export function ErrorNotice({
  title,
  message,
  inline = false,
  tone = 'danger',
  action,
}: ErrorNoticeProps): React.ReactElement {
  // Inline: the compact rose banner that sits above a form after a failed
  // submit, replacing the copy-pasted `<p class="bg-rose-50 …">` in both apps.
  if (inline) {
    const colors = tone === 'warning' ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700';
    if (action !== undefined) {
      // With an action: the message and what to do about it, on one row.
      return (
        <div
          role={tone === 'warning' ? 'status' : 'alert'}
          className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-portal-card p-3 text-portal-small ${colors}`}
        >
          <p className="min-w-0 flex-1">
            {title === undefined ? null : <b>{title} </b>}
            {message}
          </p>
          <div className="shrink-0">{action}</div>
        </div>
      );
    }
    return (
      <p role={tone === 'warning' ? 'status' : 'alert'} className={`mb-4 rounded-portal-card p-3 text-portal-small ${colors}`}>
        {message}
      </p>
    );
  }

  // Default: a centred alert panel standing in for content that failed to
  // load — a rose alert badge, headline, message and optional retry. Centred
  // and clearly a failure, not a quiet paragraph that reads like content.
  return (
    <Panel className="border-rose-100">
      <div role="alert" className="flex flex-col items-center px-6 py-12 text-center">
        <span
          aria-hidden="true"
          className="mb-5 grid size-14 place-items-center rounded-full bg-rose-500 text-[28px] leading-none font-bold text-white ring-8 ring-rose-100"
        >
          !
        </span>
        <h2 className="text-portal-card-title font-bold text-portal-ink">
          {title ?? 'Something went wrong'}
        </h2>
        <p className="mt-1.5 max-w-sm text-portal-small text-portal-muted">{message}</p>
        {action === undefined ? null : <div className="mt-6">{action}</div>}
      </div>
    </Panel>
  );
}

export interface EmptyStateProps {
  /**
   * The supporting line. When a `title` is given this is the smaller subcopy
   * beneath it; on its own it is the whole message (backwards compatible).
   */
  readonly message: string;
  /** Optional headline above the message, for a fuller first-run state. */
  readonly title?: string;
  /**
   * Optional glyph, shown in a soft badge above the text. A single character
   * (matching the shell's typographic icons) or any small node.
   */
  readonly icon?: React.ReactNode;
  readonly action?: React.ReactNode;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * The honest "nothing here yet", with an optional way to change that.
 *
 * Scales from a one-line note (message only — the inline empty rows) to a
 * proper first-run panel (icon + title + message + action) without the caller
 * choosing a layout: the presence of a title/icon is what promotes it.
 *
 * @param props - The message, and optional icon, title and call to action.
 * @returns The empty state.
 */
export function EmptyState({
  message,
  title,
  icon,
  action,
  guide,
}: EmptyStateProps): React.ReactElement {
  const promoted = title !== undefined || icon !== undefined;

  if (!promoted) {
    return (
      <div className="p-8 text-center" data-guide={guide}>
        <p className="text-portal-small text-portal-muted">{message}</p>
        {action === undefined ? null : <div className="mt-4">{action}</div>}
      </div>
    );
  }

  return (
    <div data-guide={guide} className="flex flex-col items-center px-6 py-14 text-center">
      {icon === undefined ? null : (
        <span
          aria-hidden="true"
          className="mb-5 grid size-14 place-items-center rounded-full bg-portal-sky text-brand [&>svg]:size-6"
        >
          {icon}
        </span>
      )}
      {title === undefined ? null : (
        <h2 className="text-portal-card-title font-bold text-portal-ink">{title}</h2>
      )}
      <p className="mt-1.5 max-w-sm text-portal-small text-portal-muted">{message}</p>
      {action === undefined ? null : <div className="mt-6">{action}</div>}
    </div>
  );
}
